import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import PaymentMethodSelector from "./PaymentMethodSelector";

interface JobListingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSkip: () => void;
}

const EBOOKS = [
  "The Ultimate Guide: Travel to USA, UK & Canada Without an Agent",
  "Insider's Guide to Securing Foreign University Admissions",
  "Boost Your Visa Approval Rate by 90%"
];

function pad(n: number) {
  return n.toString().padStart(2, "0");
}

export const JobListingsModal = ({
  isOpen,
  onClose,
  onSkip,
}: JobListingsModalProps) => {
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  const [showPaymentSelector, setShowPaymentSelector] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTimeLeft(15 * 60);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOpen]);

  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;

  const handleUpgradeClick = () => {
    setShowPaymentSelector(true);
  };

  const handlePaymentSelect = (method: string) => {
    setShowPaymentSelector(false);
    console.log('Selected payment method:', method);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[500px] px-0 py-0 border-0 bg-transparent shadow-none">
          <div className="rounded-2xl bg-white shadow-xl border border-gray-100 px-5 py-7 flex flex-col gap-4 relative overflow-hidden">
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-2 z-10"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>

            <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
              <span className="bg-gradient-to-r from-purple-600 to-purple-800 text-white px-3 py-1 rounded-lg font-semibold text-xs shadow animate-pulse">
                Limited Time Offer
              </span>
              <span className="flex items-center gap-1 text-xs font-mono bg-gray-100 px-2 py-1 rounded text-purple-700 border border-purple-100">
                {pad(mins)}:{pad(secs)}
              </span>
            </div>

            <DialogHeader>
              <DialogTitle className="w-full px-2 flex flex-col items-center gap-3 mt-3 mb-2">
                <span className="text-center text-3xl md:text-4xl font-extrabold text-primary tracking-tight leading-tight font-poppins">
                  Upgrade to Pro
                </span>
                <span className="block w-16 h-1 rounded-full bg-gradient-to-r from-purple-600 to-purple-800 opacity-70 mb-1"></span>
                <span className="text-base md:text-lg text-gray-900 font-medium text-center font-inter">
                  Benefit from all the following
                </span>
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-6 mt-2">
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 mt-1 w-8 h-8 flex items-center justify-center rounded-full bg-purple-100">
                    <span className="text-purple-700 font-bold text-lg">★</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">
                      Unlimited access to all Visa-Sponsored Job Listings
                    </span>
                    <div className="text-xs text-gray-500 mt-1">
                      <span className="inline-block rounded bg-purple-100 text-purple-700 px-2 py-0.5 font-medium">
                        Over 1,000,000 curated opportunities
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 mt-2">
                  <div className="flex items-center gap-3">
                    <div className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-green-100">
                      <span className="text-green-700 font-bold text-lg">✔</span>
                    </div>
                    <span className="text-gray-900 font-semibold">
                      Instantly receive <span className="text-purple-700 font-bold">3 E-books</span> worth <span className="text-green-700 font-bold">$297</span> for free:
                    </span>
                  </div>
                  <ul className="ml-11 mt-2 flex flex-col gap-1">
                    {EBOOKS.map((title, i) => (
                      <li
                        className="flex items-start text-sm md:text-sm text-gray-700 gap-2"
                        key={i}
                      >
                        <span className="mt-0.5 text-purple-600 font-bold">•</span>
                        <span>{title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="my-4" />

            <div className="flex flex-col gap-2 items-center">
              <div className="flex items-center gap-2">
                <span className="text-gray-400 line-through text-lg">$97.99</span>
                <span className="text-3xl font-extrabold text-green-700">$9.99</span>
                <span className="text-sm text-gray-500 font-medium">/mo</span>
              </div>
              <Button 
                onClick={handleUpgradeClick}
                className="w-full text-lg font-bold bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 transition text-white py-3 rounded-xl shadow-lg mt-1"
              >
                Get Offer – Upgrade Now
              </Button>
            </div>

            <button
              type="button"
              onClick={onSkip}
              className="block text-xs mt-2 text-gray-500 hover:text-purple-800 underline transition-colors duration-150 w-full text-center"
            >
              Skip offer and go to Job Listing Page
            </button>

            <div className="text-xs text-center text-gray-600 mt-2">
              We accept Credit Card, Paypal, Mobile Money, Orange Money, Mpesa & More
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PaymentMethodSelector 
        isOpen={showPaymentSelector}
        onClose={() => setShowPaymentSelector(false)}
        onSelect={handlePaymentSelect}
      />
    </>
  );
};

export default JobListingsModal;
